'use client'

import { cn, pollForElement } from '@/lib/utils'
import { useModalStatus } from '@privy-io/react-auth'
import * as Dialog from '@radix-ui/react-dialog'
import { TOKEN_PAGE_REGEX } from '@utils/routes'
import { motion } from 'motion/react'
import { usePathname, useRouter, useSelectedLayoutSegment } from 'next/navigation'
import { useEffect, useRef } from 'react'

/**
 * Wraps the token page content to display it within a modal dialogue.
 * This maintains the user's context when they navigate to a token page
 * from another part of the application (e.g., the collection page).
 *
 * It leverages Next.js parallel intercepting routes. This setup ensures that
 * if the token page URL is accessed directly (hard navigation, page refresh,
 * or linked from an external site), it renders the standard page layout
 * without this modal wrapper. Conversely, when the user performs client-side
 * navigation to a token page from elsewhere in the app, Next.js intercepts
 * the route (due to the `@tokenModal/(.)token` structure) and renders the
 * token page content using this modal wrapper component.
 */
export function TokenModalWrapper({ children }: { children: React.ReactNode }): React.ReactNode {
  const { isOpen: privyModalOpen } = useModalStatus()
  const privyModalRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (privyModalOpen) {
      // privyModalOpen is set to true before the actual
      // element is rendered so we need to poll for it
      return pollForElement<HTMLDivElement>(
        '#privy-dialog',
        (element) => {
          privyModalRef.current = element
          element.classList.add('pointer-events-auto')
        },
        10
      )
    } else {
      privyModalRef.current = null
    }
  }, [privyModalOpen])

  const pathname = usePathname()
  const router = useRouter()
  const selectedLayoutSegment = useSelectedLayoutSegment('tokenModal')
  const isIntercepting = selectedLayoutSegment !== null

  const open = isIntercepting && pathname.match(TOKEN_PAGE_REGEX)

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (!open) {
          router.back()
        }
      }}
      open={Boolean(open)}
    >
      {/* AnimatePresence mixed with parallel intercepting routes does not play nicely so we're skipping exit animations for now */}
      {/* <AnimatePresence
        onExitComplete={() => {
          console.log('EXIT COMPLETE')
        }}
      > */}
      {open ? (
        <Dialog.Portal key={'tokenModal'} forceMount>
          {/* TODO: Probably makes sense to not have bg on both overlay and content */}
          <Dialog.Overlay asChild>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn('bg-surface fixed inset-0 z-40')}
            />
          </Dialog.Overlay>
          <Dialog.Content
            asChild
            onEscapeKeyDown={(e) => {
              if (privyModalOpen) {
                e.preventDefault()
                return
              }
            }}
            onInteractOutside={(e) => {
              // We want to allow interaction with the Privy modal without
              // closing the token page modal.
              if (privyModalRef.current?.contains(e.target as Node)) {
                e.preventDefault()
                return
              }
            }}
            trapFocus={!privyModalOpen}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn('bg-surface fixed inset-0 z-50 overflow-auto')}
            >
              {children}
            </motion.div>
          </Dialog.Content>
        </Dialog.Portal>
      ) : null}
      {/* </AnimatePresence> */}
    </Dialog.Root>
  )
}
